import Phaser from 'phaser';
import { CONSTANTS } from '../game.js';
import { EventTypes } from '../events/EventTypes.js';

/**
 * 物理演算に基づく錘の挙動を制御するクラス
 */
export class Weight {
    // パーフェクト状態のアニメーション用の定数
    static PERFECT_ANIMATION = {
        PULSE_DURATION: 800, // 脈動アニメーションの周期（ミリ秒）
        FLAME_EMIT_INTERVAL: 50, // 炎パーティクル放出間隔（ミリ秒）
        FLAME_QUANTITY: 3, // 一度に放出する炎パーティクルの数
        GLOW_COLOR: 0xff3300, // グロー効果の色（赤橙色）
        GLOW_ALPHA: 0.6 // グロー効果の透明度
    };
    /**
     * @param {Phaser.Scene} scene - シーン
     * @param {number} x - 初期X座標
     * @param {number} y - 初期Y座標
     * @param {Object} callbacks - コールバック関数のオブジェクト
     * @param {Function} [callbacks.onBodyUpdated] - ボディ更新時のコールバック
     * @param {Function} [callbacks.onTargetHit] - ターゲット衝突時のコールバック
     * @param {Function} [callbacks.onPerfectStateChanged] - パーフェクト状態変更時のコールバック
     */
    constructor(scene, x, y, callbacks = {}) {
        this.scene = scene;
        this.callbacks = callbacks;
        
        // 基本パラメータ
        this.radius = CONSTANTS.WEIGHT.RADIUS;  // 半径
        this.mass = CONSTANTS.WEIGHT.MASS;      // 質量
        
        // エフェクト関連
        this.lastBounceEffectTime = 0;  // 最後にエフェクトを表示した時刻
        this.lastFlameEmitTime = 0;     // 最後に炎エフェクトを放出した時刻
        
        // ゴム紐切れフラグ
        this.isDetached = false;
        
        // パーフェクト状態の管理
        this.isPerfect = false;
        
        // 重りの色
        this.weightColor = 0x333333;
        this.weightStrokeColor = 0x000000;
        this.originalWeightColor = this.weightColor; // 元の色を保存
        
        // 錘の物理オブジェクト作成
        this.body = scene.matter.add.circle(
            x,
            y,
            this.radius,
            {
                label: 'weight',
                mass: this.mass,
                friction: CONSTANTS.WEIGHT.FRICTION,
                restitution: CONSTANTS.WEIGHT.RESTITUTION,
                frictionAir: CONSTANTS.WEIGHT.FRICTION_AIR // 空気抵抗を追加
            }
        );
        
        // 錘の視覚的表現
        this.sprite = scene.add.circle(0, 0, this.radius, this.weightColor);
        this.sprite.setStrokeStyle(2, this.weightStrokeColor);
        this.sprite.setDepth(15); // コンボ表示より前面に表示
        
        // グロー効果用のスプライト（初期状態では非表示）
        this.glowSprite = scene.add.circle(0, 0, this.radius * 1.5, Weight.PERFECT_ANIMATION.GLOW_COLOR);
        this.glowSprite.setAlpha(0);
        this.glowSprite.setDepth(14); // 重りの背後に表示
        
        // 炎エフェクト用のパーティクルエミッター
        if (scene.textures.exists('flame')) {
            this.flameEmitter = scene.add.particles(0, 0, 'flame', {
                lifespan: 600,
                speed: { min: 50, max: 100 },
                scale: { start: 0.4, end: 0 },
                alpha: { start: 0.8, end: 0 },
                blendMode: 'ADD',
                emitting: false
            });
            this.flameEmitter.setDepth(16); // 重りより前面に表示
        } else {
            console.warn('Weight: flameテクスチャが見つかりません');
            this.flameEmitter = null;
        }
        
        // 物理オブジェクトとスプライトの関連付け
        this.body.gameObject = this.sprite;
        
        // 衝突イベントの購読
        this.setupCollisionEvents();
        
        // 脈動アニメーションの設定（初期状態では無効）
        this.pulseAnimation = null;
    }
    
    /**
     * 衝突イベントの購読設定
     */
    setupCollisionEvents() {
        // カスタムイベントを購読
        this.scene.events.on(EventTypes.COLLISION.WEIGHT_TARGET, this.onWeightTargetCollision, this);
    }
    
    /**
     * 錘と的の衝突イベントハンドラ
     * @param {Matter.Body} weightBody - 錘の物理ボディ
     * @param {Matter.Body} targetBody - 的の物理ボディ
     */
    onWeightTargetCollision(weightBody, targetBody) {
        // 自分の錘との衝突かどうかを確認
        if (weightBody === this.body) {
            this.onTargetHit(targetBody);
        }
    }
    
    
    onTargetHit(target) {
        // 衝突エフェクト
        this.createHitEffect(target.position.x, target.position.y);
        
        // 効果音の再生（実装時にコメントを外す）
        // this.scene.sound.play('hit');
        
        // コールバック関数を呼び出す
        if (this.callbacks.onTargetHit) {
            this.callbacks.onTargetHit(target);
        }
    }
    
    /**
     * 衝突エフェクトを作成
     * @param {number} x - エフェクトのX座標
     * @param {number} y - エフェクトのY座標
     */
    createHitEffect(x, y) {
        const particles = this.scene.add.particles(x, y, 'target', {
            speed: { min: 50, max: 200 },
            angle: { min: 0, max: 360 },
            scale: { start: 0.5, end: 0 },
            lifespan: 500,
            quantity: 10,
            blendMode: 'ADD',
            emitting: false,
            tint: this.weightColor
        });
        
        particles.setDepth(16);
        particles.explode(10, 0, 0);
        
        this.scene.time.delayedCall(600, () => {
            particles.destroy();
        });
    }

    /**
     * パーフェクト状態を設定
     * @param {boolean} isPerfect - パーフェクト状態かどうか
     */
    setPerfectState(isPerfect) {
        // 状態が変わらない場合は何もしない
        if (this.isPerfect === isPerfect) return;
        
        this.isPerfect = isPerfect;
        
        if (isPerfect) {
            // パーフェクト状態に入った時の処理
            // console.log('重りがパーフェクト状態に入りました');
            
            // 重りの色を変更（赤く光る）
            this.sprite.fillColor = 0xff3300;
            
            // グロー効果を表示
            this.glowSprite.setAlpha(Weight.PERFECT_ANIMATION.GLOW_ALPHA);
            
            // 脈動アニメーションを開始
            this.startPulseAnimation();
            
            // コールバック関数を呼び出す
            if (this.callbacks.onPerfectStateChanged) {
                this.callbacks.onPerfectStateChanged(true);
            }
        } else {
            // パーフェクト状態から抜けた時の処理
            // console.log('重りがパーフェクト状態から抜けました');
            
            // 重りの色を元に戻す
            this.sprite.fillColor = this.originalWeightColor;
            
            // グロー効果を非表示
            this.glowSprite.setAlpha(0);
            
            // 脈動アニメーションを停止
            this.stopPulseAnimation();
            
            // コールバック関数を呼び出す
            if (this.callbacks.onPerfectStateChanged) {
                this.callbacks.onPerfectStateChanged(false);
            }
        }
    }
    
    /**
     * 脈動アニメーションを開始
     */
    startPulseAnimation() {
        // 既存のアニメーションがあれば停止
        this.stopPulseAnimation();
        
        // 脈動アニメーション（サイズが周期的に変化）
        this.pulseAnimation = this.scene.tweens.add({
            targets: [this.sprite, this.glowSprite],
            scale: { from: 1, to: 1.2 },
            duration: Weight.PERFECT_ANIMATION.PULSE_DURATION / 2,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }
    
    /**
     * 脈動アニメーションを停止
     */
    stopPulseAnimation() {
        if (this.pulseAnimation) {
            this.pulseAnimation.stop();
            this.pulseAnimation = null;
            
            // スケールを元に戻す
            this.sprite.setScale(1);
            this.glowSprite.setScale(1);
        }
    }
    
    /**
     * 炎エフェクトを放出
     */
    emitFlameEffect() {
        if (!this.flameEmitter || !this.isPerfect) return;
        
        const currentTime = this.scene.time.now;
        if (currentTime - this.lastFlameEmitTime < Weight.PERFECT_ANIMATION.FLAME_EMIT_INTERVAL) return;
        
        this.lastFlameEmitTime = currentTime;
        
        // 重りの位置に炎エフェクトを放出
        this.flameEmitter.setPosition(this.body.position.x, this.body.position.y);
        
        // 上向きに炎を放出
        this.flameEmitter.setEmitZone({
            type: 'edge',
            source: new Phaser.Geom.Circle(0, 0, this.getCurrentRadius()),
            quantity: Weight.PERFECT_ANIMATION.FLAME_QUANTITY,
            yoyo: false
        });
        
        // 炎の方向を上向きに設定
        this.flameEmitter.setAngle({ min: 270 - 30, max: 270 + 30 });
        
        // 炎を放出
        this.flameEmitter.explode(Weight.PERFECT_ANIMATION.FLAME_QUANTITY);
    }

    update() {
        if (!this.body || !this.body.position) return;

        // 画面外に出ないように位置を制限
        const screenWidth = this.scene.game.config.width;
        const screenHeight = this.scene.game.config.height;

        // 半径を考慮して位置を制限
        const newX = Phaser.Math.Clamp(
            this.body.position.x,
            this.radius,
            screenWidth - this.radius
        );
        const newY = Phaser.Math.Clamp(
            this.body.position.y,
            this.radius,
            screenHeight - this.radius
        );

        // 位置が画面外の場合は修正
        if (newX !== this.body.position.x || newY !== this.body.position.y) {
            // 現在の速度を取得
            const velocity = this.body.velocity;
            const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
            
            // 画面端での反発を計算
            const newVelocity = { x: velocity.x, y: velocity.y };
            
            if (newX !== this.body.position.x) {
                newVelocity.x = -velocity.x * this.body.restitution;
            }
            if (newY !== this.body.position.y) {
                newVelocity.y = -velocity.y * this.body.restitution;
            }

            // 位置と速度を更新
            this.scene.matter.body.setPosition(this.body, { x: newX, y: newY });
            this.scene.matter.body.setVelocity(this.body, newVelocity);
        }

        // スプライトを追従させる
        this.sprite.x = this.body.position.x;
        this.sprite.y = this.body.position.y;
        this.sprite.rotation = this.body.angle;
        
        // グロースプライトも追従させる
        this.glowSprite.x = this.body.position.x;
        this.glowSprite.y = this.body.position.y;
        
        // パーフェクト状態の場合は炎エフェクトを放出
        if (this.isPerfect) {
            this.emitFlameEffect();
        }
    }
    
    /**
     * 現在の半径を取得
     * @returns {number} 現在の半径
     */
    getCurrentRadius() {
        return this.radius;
    }
    
    getBody() {
        return this.body;
    }
    
    getPosition() {
        return this.body.position;
    }
    
    setPosition(x, y) {
        this.scene.matter.body.setPosition(this.body, { x, y });
    }
    
    applyForce(force) {
        this.scene.matter.body.applyForce(this.body, this.body.position, force);
    }
    
    /**
     * ゴム紐が切れた時の処理
     */
    handleRubberBreak() {
        if (this.isDetached) return;
        
        this.isDetached = true;
        
        try {
            // 古い物理ボディの位置と速度を保存
            const position = this.body.position;
            const velocity = this.body.velocity;
            const oldBody = this.body;
            
            // 古い物理ボディを削除
            this.scene.matter.world.remove(this.body);
            
            // 新しい物理ボディを作成（物理特性を変更）
            this.body = this.scene.matter.add.circle(
                position.x,
                position.y,
                this.radius,
                {
                    label: 'weight',
                    mass: this.body.mass,
                    friction: 0.1,           // 摩擦を下げる
                    restitution: 1.2,        // 反発係数を上げる
                    frictionAir: 0.001       // 空気抵抗を下げる
                }
            );
            
            // 速度を復元して少し上向きの力を加える
            const newVelocity = {
                x: velocity.x * 1.2,  // 現在の横方向の速度を少し強める
                y: velocity.y - 3     // 上向きの力を加える
            };
            this.scene.matter.body.setVelocity(this.body, newVelocity);
            
            // 物理オブジェクトとスプライトの関連付け
            this.body.gameObject = this.sprite;
            
            console.log('ゴム紐切れ時の物理特性を変更しました');
        } catch (error) {
            console.error('ゴム紐切れ時の物理特性変更に失敗しました:', error);
        }
        
        // スプライトの回転アニメーション
        this.scene.tweens.add({
            targets: this.sprite,
            angle: this.sprite.angle + 360 * 2, // 2回転
            duration: 1000,
            ease: 'Power1'
        });
    }

    destroy() {
        // イベントリスナーのクリーンアップ
        this.scene.events.off(EventTypes.COLLISION.WEIGHT_TARGET, this.onWeightTargetCollision, this);
        
        // アニメーションの停止
        this.stopPulseAnimation();
        
        // パーティクルエミッターの破棄
        if (this.flameEmitter) {
            this.flameEmitter.destroy();
            this.flameEmitter = null;
        }
        
        // 物理ボディの削除
        this.scene.matter.world.remove(this.body);
        
        // スプライトの削除
        this.sprite.destroy();
        this.glowSprite.destroy();
    }
}
